import { useEffect, useState, useCallback } from 'react';
import { supabase } from '@/lib/supabase';
import { Database } from '@/types/supabase';

export type TeamMeditation =
  Database['public']['Tables']['team_meditations']['Row'];

type InsertTeamMeditation =
  Database['public']['Tables']['team_meditations']['Insert'];

export function useTeamMeditations(teamId: string | undefined) {
  const [meditations, setMeditations] = useState<TeamMeditation[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeMeditations, setActiveMeditations] =
    useState<TeamMeditation[]>([]);

  const fetchMeditations = useCallback(async () => {
    if (!teamId) return;
    setLoading(true);

    const { data, error } = await supabase
      .from('team_meditations')
      .select('*')
      .eq('team_id', teamId)
      .order('scheduled_at', { ascending: true });

    if (!error && data) {
      const now = new Date();
      const relevant = data.filter((m) => {
        const end = new Date(m.scheduled_at);
        end.setMinutes(end.getMinutes() + (m.duration_minutes ?? 30));
        return end > now;
      });
      setMeditations(relevant);

      // Find all currently active meditations (open player 30s early)
      const active = relevant.filter((m) => {
        const start = new Date(m.scheduled_at);
        start.setSeconds(start.getSeconds() - 30);
        const end = new Date(m.scheduled_at);
        end.setMinutes(end.getMinutes() + (m.duration_minutes ?? 30));
        return now >= start && now < end;
      });
      setActiveMeditations(active);
    }
    setLoading(false);
  }, [teamId]);

  useEffect(() => {
    fetchMeditations();

    // Re-check every 5 seconds to detect when a meditation becomes active
    const interval = setInterval(fetchMeditations, 5_000);
    return () => clearInterval(interval);
  }, [fetchMeditations]);

  const createMeditation = async (meditation: InsertTeamMeditation) => {
    const { data, error } = await supabase
      .from('team_meditations')
      .insert(meditation)
      .select()
      .single();

    if (error) throw error;
    await fetchMeditations();
    return data;
  };

  const deleteMeditation = async (id: string) => {
    const { error } = await supabase
      .from('team_meditations')
      .delete()
      .eq('id', id);

    if (error) throw error;
    await fetchMeditations();
  };

  const activeMeditation = activeMeditations[0] ?? null;
  const upcomingMeditations = meditations.filter(
    (m) => new Date(m.scheduled_at) > new Date(),
  );
  const hasUpcoming = upcomingMeditations.length > 0 || activeMeditations.length > 0;
  const nextMeditation = upcomingMeditations[0] ?? null;

  return {
    meditations,
    loading,
    hasUpcoming,
    nextMeditation,
    activeMeditation,
    activeMeditations,
    createMeditation,
    deleteMeditation,
    refetch: fetchMeditations,
  };
}
